const SENSITIVE_KEY_PATTERN = /(?:^error$|^error[_-]?message$|token|secret|password|authorization|cookie|webhook|raw|payload|merchant|amount|balance|spend|budget|income|salary|payment|price|cost|cash|debt|asset|liabilit|net[_-]?worth|credit[_-]?limit|available[_-]?credit|forecast|overrun|transactions?[_-]?ids?|accounts?$|account[_-]?(?:id|ids|name|number|mask|balance|balances)|institution|plaid|quiltt|refresh|access|email|phone|address|ssn|routing|mask|session[_-]?id|link[_-]?session[_-]?id|item[_-]?id|connection[_-]?id|connector[_-]?id|customer[_-]?id|subscription[_-]?id|invoice[_-]?id|payment[_-]?method[_-]?id|setup[_-]?intent[_-]?id|app[_-]?account[_-]?token|household[_-]?id|user[_-]?id|member[_-]?id|invite)/i;
const SENSITIVE_VALUE_PATTERN = /(?:[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}|bearer\s+[A-Za-z0-9._-]+|access[-_]?token|refresh[-_]?token|public[-_]?token|client[-_]?secret|webhook[-_]?secret|sk[-_](?:live|test)[-_][A-Za-z0-9._-]+|\b(?:sub|cus|in|evt|pi|pm|seti|si|txn|ch|cs)_[a-zA-Z0-9_=-]{6,}\b|\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b|(?:\$|USD|CAD)\s*\d)/i;
const REDACTED = '[redacted]';
const WEB_VITAL_THRESHOLDS = Object.freeze({
  CLS: Object.freeze({ good: 0.1, poor: 0.25 }),
  FCP: Object.freeze({ good: 1800, poor: 3000 }),
  FID: Object.freeze({ good: 100, poor: 300 }),
  INP: Object.freeze({ good: 200, poor: 500 }),
  LCP: Object.freeze({ good: 2500, poor: 4000 }),
  TTFB: Object.freeze({ good: 800, poor: 1800 }),
});

const getNow = () => {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
};

const sanitizePrimitive = (key, value) => {
  if (SENSITIVE_KEY_PATTERN.test(String(key || ''))) {
    return REDACTED;
  }
  if (typeof value === 'string' && SENSITIVE_VALUE_PATTERN.test(value)) {
    return REDACTED;
  }
  if (typeof value === 'string' && value.length > 200) {
    return `${value.slice(0, 200)}...`;
  }
  return value;
};

const sanitizeInstrumentationPayload = (payload, parentKey = '') => {
  if (payload == null) return payload;
  if (typeof payload !== 'object') {
    return sanitizePrimitive(parentKey, payload);
  }
  if (Array.isArray(payload)) {
    return payload.slice(0, 20).map((item) => sanitizeInstrumentationPayload(item, parentKey));
  }

  return Object.entries(payload).reduce((safePayload, [key, value]) => {
    if (SENSITIVE_KEY_PATTERN.test(key)) {
      safePayload[key] = REDACTED;
      return safePayload;
    }
    safePayload[key] = sanitizeInstrumentationPayload(value, key);
    return safePayload;
  }, {});
};

const stripQueryAndHash = (value = '') => String(value).split('?')[0].split('#')[0];

const replaceSensitivePathSegments = (value = '') => stripQueryAndHash(value)
  .replace(/\/[^/?#@/]+@[^/?#@/]+\.[^/?#@/]+(?=\/|$)/g, '/:id')
  .replace(/\/(users?|households?|accounts?|transactions?|sessions?|items?|connections?|bank-connections)\/[A-Za-z0-9._:-]{8,}(?=\/|$)/gi, '/$1/:id')
  .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, ':id')
  .replace(/\/\d+(?=\/|$)/g, '/:id')
  .replace(/\/\d{4}-\d{2}(?=\/|$)/g, '/:month')
  .replace(/\/(?:access|refresh|public|link|handoff|webhook)[_-]?[A-Za-z0-9._-]{8,}(?=\/|$)/gi, '/:token');

const normalizeInstrumentationEventName = (eventName = 'event') => {
  const rawName = String(eventName || 'event');
  if (SENSITIVE_VALUE_PATTERN.test(rawName)) {
    return 'event';
  }

  const normalized = rawName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_:-]+/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+|_+$/g, '');

  if (!normalized || SENSITIVE_VALUE_PATTERN.test(normalized)) {
    return 'event';
  }

  return normalized.slice(0, 80);
};

const normalizeRouteForTelemetry = (route = '') => {
  const pathname = typeof route === 'string' ? route : route?.pathname || '';
  const normalized = replaceSensitivePathSegments(pathname || '/');
  return normalized || '/';
};

const normalizeEndpointForTelemetry = (endpoint = '') => {
  if (typeof endpoint !== 'string') return 'unknown';
  try {
    if (endpoint.startsWith('http')) {
      const parsed = new URL(endpoint);
      return replaceSensitivePathSegments(parsed.pathname || '/');
    }
  } catch (_error) {
    // Fall through to plain string normalization.
  }
  return replaceSensitivePathSegments(endpoint || '/');
};

const getWebVitalRating = (metricName, value) => {
  const thresholds = WEB_VITAL_THRESHOLDS[String(metricName || '').toUpperCase()];
  const numericValue = Number(value);
  if (!thresholds || !Number.isFinite(numericValue)) return null;
  if (numericValue > thresholds.poor) return 'poor';
  if (numericValue > thresholds.good) return 'needs-improvement';
  return 'good';
};

const emitInstrumentationEvent = (eventName, payload = {}) => {
  const safeEventName = normalizeInstrumentationEventName(eventName);
  const safePayload = sanitizeInstrumentationPayload({
    ...payload,
    eventName: safeEventName,
    recordedAt: new Date().toISOString(),
  });

  if (typeof window !== 'undefined') {
    const sink = window.__VYLO_INSTRUMENTATION__;
    if (typeof sink === 'function') {
      sink(safeEventName, safePayload);
    }
    if (typeof window.dispatchEvent === 'function' && typeof window.CustomEvent === 'function') {
      window.dispatchEvent(new window.CustomEvent('vylo:instrumentation', {
        detail: {
          eventName: safeEventName,
          payload: safePayload,
        },
      }));
    }
  }

  return safePayload;
};

const recordWebVital = (metric = {}) => {
  const allowedMetricNames = new Set(['CLS', 'FCP', 'FID', 'INP', 'LCP', 'TTFB']);
  const name = String(metric.name || '').toUpperCase();
  if (!allowedMetricNames.has(name)) return null;
  const value = Number(metric.value);
  if (!Number.isFinite(value)) return null;
  const target = WEB_VITAL_THRESHOLDS[name]?.good ?? null;

  return emitInstrumentationEvent('web_vital', {
    metric: name,
    value,
    rating: metric.rating || getWebVitalRating(name, value),
    target,
    exceedsGoodThreshold: target == null ? null : value > target,
    id: metric.id || null,
    delta: Number.isFinite(Number(metric.delta)) ? Number(metric.delta) : null,
    navigationType: metric.navigationType || null,
  });
};

const recordRouteTransition = ({ from, to, durationMs } = {}) => emitInstrumentationEvent('route_transition', {
  from: normalizeRouteForTelemetry(from),
  to: normalizeRouteForTelemetry(to),
  durationMs: Number.isFinite(Number(durationMs)) ? Number(durationMs) : null,
});

const createRouteTransitionTimer = ({ from, to } = {}) => {
  const startedAt = getNow();
  return () => recordRouteTransition({
    from,
    to,
    durationMs: Math.max(0, getNow() - startedAt),
  });
};

const recordApiBreadcrumb = (eventName, {
  endpoint,
  method = 'GET',
  status = null,
  durationMs = null,
  retried = false,
} = {}) => emitInstrumentationEvent(`api_${eventName}`, {
  endpoint: normalizeEndpointForTelemetry(endpoint),
  method: String(method || 'GET').toUpperCase(),
  status,
  durationMs: Number.isFinite(Number(durationMs)) ? Number(durationMs) : null,
  retried: retried === true,
});

const recordImportBreadcrumb = (eventName, {
  phase = null,
  aiPhase = null,
  canEnterDashboard = null,
  reviewReady = null,
  historicalImportComplete = null,
  failed = null,
  retryAvailable = null,
} = {}) => emitInstrumentationEvent(`import_${eventName}`, {
  phase,
  aiPhase,
  canEnterDashboard,
  reviewReady,
  historicalImportComplete,
  failed,
  retryAvailable,
});

const recordAccountReconciliationBreadcrumb = (eventName, {
  candidateCount = null,
  visibilityHeld = null,
  resolution = null,
  source = null,
} = {}) => emitInstrumentationEvent(`account_reconciliation_${eventName}`, {
  candidateCount: Number.isFinite(Number(candidateCount)) ? Number(candidateCount) : null,
  visibilityHeld: typeof visibilityHeld === 'boolean' ? visibilityHeld : null,
  resolution: ['use_existing', 'keep_both'].includes(resolution) ? resolution : null,
  source: ['accounts_visit', 'post_connect', 'manage', 'modal'].includes(source) ? source : null,
});

const recordFinancialSemanticsBreadcrumb = (eventName, {
  semantic = null,
  source = null,
  role = null,
  excludedSurface = null,
} = {}) => emitInstrumentationEvent(`financial_semantics_${eventName}`, {
  semantic,
  source,
  role,
  excludedSurface,
});

const recordNetWorthBreadcrumb = (eventName, {
  cache = null,
  deduplicated = null,
  trigger = null,
  initialSync = null,
  unknown = null,
  fresh = null,
  recent = null,
  refreshing = null,
  stale = null,
  providerError = null,
} = {}) => emitInstrumentationEvent(`net_worth_${eventName}`, {
  cache: ['hit', 'miss', 'disabled'].includes(cache) ? cache : null,
  deduplicated: typeof deduplicated === 'boolean' ? deduplicated : null,
  trigger: ['manual', 'setup', 'post_connect'].includes(trigger) ? trigger : null,
  initialSync,
  unknown,
  fresh,
  recent,
  refreshing,
  stale,
  providerError,
});

export {
  createRouteTransitionTimer,
  emitInstrumentationEvent,
  getWebVitalRating,
  normalizeInstrumentationEventName,
  normalizeEndpointForTelemetry,
  normalizeRouteForTelemetry,
  recordApiBreadcrumb,
  recordAccountReconciliationBreadcrumb,
  recordFinancialSemanticsBreadcrumb,
  recordNetWorthBreadcrumb,
  recordImportBreadcrumb,
  recordRouteTransition,
  recordWebVital,
  sanitizeInstrumentationPayload,
  WEB_VITAL_THRESHOLDS,
};
